/**
 * 手机选点：精度阈值、三次取样、自动/手工点与直线距离
 */

export const PICK_ACCURACY_TOO_LARGE_M = 50;
export const PICK_ACCURACY_RECORD_MAX_M = 15;
export const PICK_SAMPLE_INTERVAL_MS = 5000;
export const PICK_SAMPLE_COUNT = 3;
/** 三次取样：蓝 / 绿 / 紫 */
export const PICK_SAMPLE_COLORS = ['#2563eb', '#16a34a', '#a855f7'];
/** 自动最终点（指挥端既有橙色） */
export const PICK_AUTO_COLOR = '#f97316';
/** 前线手工改点 */
export const PICK_MANUAL_COLOR = '#0284c7';

export function normalizeCaptureThresholds(raw = {}) {
  const recordMax = Number(raw?.recordAccuracyMaxM ?? raw?.record_accuracy_max_m);
  const tooLarge = Number(raw?.tooLargeAccuracyM ?? raw?.too_large_accuracy_m);
  const recordAccuracyMaxM = Number.isFinite(recordMax) && recordMax > 0
    ? Math.min(100, Math.max(3, Math.round(recordMax)))
    : PICK_ACCURACY_RECORD_MAX_M;
  let tooLargeAccuracyM = Number.isFinite(tooLarge) && tooLarge > 0
    ? Math.min(500, Math.max(10, Math.round(tooLarge)))
    : PICK_ACCURACY_TOO_LARGE_M;
  if (tooLargeAccuracyM <= recordAccuracyMaxM) {
    tooLargeAccuracyM = Math.min(500, recordAccuracyMaxM + 10);
  }
  return { recordAccuracyMaxM, tooLargeAccuracyM };
}

/**
 * @param {number} meters
 * @param {{ recordAccuracyMaxM?: number, tooLargeAccuracyM?: number }} [thresholds]
 * @returns {'unknown' | 'too_large' | 'waiting' | 'ready'}
 */
export function classifyAccuracy(meters, thresholds = {}) {
  const { recordAccuracyMaxM, tooLargeAccuracyM } = normalizeCaptureThresholds(thresholds);
  if (meters == null || meters === '') return 'unknown';
  const value = Number(meters);
  if (!Number.isFinite(value) || value < 0) return 'unknown';
  if (value > tooLargeAccuracyM) return 'too_large';
  if (value < recordAccuracyMaxM) return 'ready';
  return 'waiting';
}

export function normalizeSamples(samples) {
  if (!Array.isArray(samples)) return [];
  return samples
    .map((item, index) => {
      const lat = Number(item?.lat ?? item?.latitude);
      const lng = Number(item?.lng ?? item?.longitude);
      if (!isValidPickLngLat(lng, lat)) return null;
      const seq = Number.parseInt(item?.seq, 10) || index + 1;
      const accuracy = Number(item?.accuracy);
      return {
        seq,
        lat,
        lng,
        accuracy: Number.isFinite(accuracy) ? accuracy : null,
        color: item?.color || PICK_SAMPLE_COLORS[Math.max(0, seq - 1)] || PICK_SAMPLE_COLORS[0],
        captured_at: item?.captured_at || null
      };
    })
    .filter(Boolean)
    .sort((a, b) => a.seq - b.seq);
}

/** 精度数值越小越好 */
export function pickBestSample(samples) {
  const list = normalizeSamples(samples);
  if (!list.length) return null;
  return list.reduce((best, item) => {
    const bestAcc = Number(best?.accuracy);
    const nextAcc = Number(item.accuracy);
    if (!Number.isFinite(bestAcc)) return item;
    if (!Number.isFinite(nextAcc)) return best;
    return nextAcc < bestAcc ? item : best;
  });
}

/** Number(null) 为 0，不能当坐标 */
export function parseCoord(value) {
  if (value == null || value === '') return null;
  const number = Number(value);
  return Number.isFinite(number) ? number : null;
}

/** 拒绝空值与 (0,0)，避免未改点被当成几内亚湾 */
export function isValidPickLngLat(lng, lat) {
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return false;
  if (Math.abs(lat) > 90 || Math.abs(lng) > 180) return false;
  if (lat === 0 && lng === 0) return false;
  return true;
}

export function hasMeaningfulManualPoint(autoPoint, manualPoint, minMeters = 1) {
  if (!isValidPickLngLat(manualPoint?.lng, manualPoint?.lat)) return false;
  if (!isValidPickLngLat(autoPoint?.lng, autoPoint?.lat)) return true;
  const meters = haversineMeters(autoPoint, manualPoint);
  return Number.isFinite(meters) && meters >= minMeters;
}

/** 统一输出「经度, 纬度」，与救援目标粘贴格式一致 */
export function formatLngLat(point) {
  if (!point) return '';
  const lat = parseCoord(point.lat ?? point.latitude);
  const lng = parseCoord(point.lng ?? point.longitude);
  if (!isValidPickLngLat(lng, lat)) return '';
  return `${lng.toFixed(6)}, ${lat.toFixed(6)}`;
}

/**
 * 底部消息框文案
 * @param {{
 *   phase: string,
 *   seq?: number,
 *   accuracy?: number,
 *   autoPoint?: { lat: number, lng: number },
 *   manualPoint?: { lat: number, lng: number },
 *   recordAccuracyMaxM?: number
 * }} state
 */
export function buildCaptureStatusText(state = {}) {
  const { phase, seq, accuracy, autoPoint, manualPoint } = state;
  const { recordAccuracyMaxM } = normalizeCaptureThresholds(state);
  const meters = Number.isFinite(Number(accuracy)) ? Math.round(Number(accuracy)) : null;

  if (phase === 'locating') return '正在获取当前位置…';
  if (phase === 'too_large') {
    return meters != null ? `精度误差过大（当前约 ${meters} 米）` : '精度误差过大';
  }
  if (phase === 'waiting') {
    return meters != null
      ? `当前精度约 ${meters} 米，需小于 ${recordAccuracyMaxM} 米后开始记录`
      : `等待定位精度小于 ${recordAccuracyMaxM} 米后开始记录`;
  }
  if (phase === 'recording') {
    const n = Number.parseInt(seq, 10) || 1;
    const accText = meters != null ? `${meters}` : '—';
    return `正在获取第 ${n} 次坐标，精度 ${accText} 米。`;
  }
  const autoText = formatLngLat(autoPoint);
  if (phase === 'preview') {
    return autoText
      ? `已选出精度最好的点：${autoText}（经度, 纬度）。请确认提交。`
      : '三次取样完成，请确认提交。';
  }
  if (phase === 'editing') {
    return autoText
      ? `当前自动点：${autoText}（经度, 纬度）。请在地图上点选或拖动修改位置。`
      : '请在地图上点选手工位置。';
  }
  if (phase === 'amended' && manualPoint && autoText) {
    return `已确定最终坐标为：${autoText}。手工位置：${formatLngLat(manualPoint)}`;
  }
  if ((phase === 'finalized' || phase === 'amended') && autoText) {
    return `已确定最终坐标为：${autoText}（经度, 纬度）`;
  }
  return '';
}

export function haversineMeters(a, b) {
  const lat1 = Number(a?.lat ?? a?.latitude);
  const lng1 = Number(a?.lng ?? a?.longitude);
  const lat2 = Number(b?.lat ?? b?.latitude);
  const lng2 = Number(b?.lng ?? b?.longitude);
  if (![lat1, lng1, lat2, lng2].every(Number.isFinite)) return null;
  const R = 6371000;
  const φ1 = (lat1 * Math.PI) / 180;
  const φ2 = (lat2 * Math.PI) / 180;
  const dφ = ((lat2 - lat1) * Math.PI) / 180;
  const dλ = ((lng2 - lng1) * Math.PI) / 180;
  const x = Math.sin(dφ / 2) ** 2 + Math.cos(φ1) * Math.cos(φ2) * Math.sin(dλ / 2) ** 2;
  return 2 * R * Math.atan2(Math.sqrt(x), Math.sqrt(1 - x));
}

export function pickPointShareCopy(note) {
  const trimmed = String(note || '').trim();
  return {
    title: 'ZHXT 手机选点',
    description: trimmed
      ? `${trimmed} · 请打开后上报当前位置`
      : '指挥中心邀请你上报当前位置，点开即可定位'
  };
}

export function buildPickPointShareUrl(origin, shortCode, token) {
  const root = String(origin || '').replace(/\/$/, '');
  if (shortCode) return `${root}/p/${encodeURIComponent(shortCode)}`;
  if (token) return `${root}/m/pick?t=${encodeURIComponent(token)}`;
  return '';
}

export function formatStraightDistance(meters) {
  const value = Number(meters);
  if (!Number.isFinite(value) || value < 0) return '';
  if (value < 1000) return `直线距离 ${Math.round(value)} 米`;
  return `直线距离 ${(value / 1000).toFixed(2)} 公里`;
}

export function pickPointMidpoint(a, b) {
  const lat1 = Number(a?.lat ?? a?.latitude);
  const lng1 = Number(a?.lng ?? a?.longitude);
  const lat2 = Number(b?.lat ?? b?.latitude);
  const lng2 = Number(b?.lng ?? b?.longitude);
  if (![lat1, lng1, lat2, lng2].every(Number.isFinite)) return null;
  return { lng: (lng1 + lng2) / 2, lat: (lat1 + lat2) / 2 };
}

export function resolveAutoPoint(point) {
  if (!point) return null;
  const lat = parseCoord(point.auto_lat ?? point.used_lat);
  const lng = parseCoord(point.auto_lng ?? point.used_lng);
  if (!isValidPickLngLat(lng, lat)) return null;
  const accuracy = Number(point.auto_accuracy ?? point.used_accuracy);
  return {
    lat,
    lng,
    accuracy: Number.isFinite(accuracy) ? accuracy : null
  };
}

export function resolveManualPoint(point) {
  if (!point) return null;
  const lat = parseCoord(point.manual_lat);
  const lng = parseCoord(point.manual_lng);
  if (!isValidPickLngLat(lng, lat)) return null;
  return { lat, lng };
}

/** 状态码中文映射 */
const STATUS_LABELS = {
  active: '有效',
  used: '已使用',
  expired: '已过期',
  revoked: '已撤销',
  deleted: '已删除'
};

/** 救援推送列表状态摘要 */
export function formatPickPointListDetail(item, thresholds = {}) {
  if (!item) return '';
  const { recordAccuracyMaxM, tooLargeAccuracyM } = normalizeCaptureThresholds(thresholds);
  const liveAcc = Number(item.live_accuracy);
  const phase = item.capture_phase;
  const samples = normalizeSamples(item.capture_samples);
  if (item.status === 'active' || phase === 'waiting' || phase === 'sample') {
    if (Number.isFinite(liveAcc) && liveAcc > tooLargeAccuracyM) {
      return `精度误差过大（${Math.round(liveAcc)} 米）`;
    }
    if (samples.length > 0 && samples.length < PICK_SAMPLE_COUNT) {
      const acc = Number.isFinite(liveAcc) ? Math.round(liveAcc) : Math.round(samples[samples.length - 1]?.accuracy);
      return `取样 ${samples.length}/${PICK_SAMPLE_COUNT}${Number.isFinite(acc) ? ` 精度 ${acc} 米` : ''}`;
    }
    if (Number.isFinite(liveAcc) && liveAcc >= recordAccuracyMaxM) {
      return `等待精度 ${Math.round(liveAcc)} 米`;
    }
    if (Number.isFinite(liveAcc)) {
      return `定位中 ${Math.round(liveAcc)} 米`;
    }
  }
  const auto = resolveAutoPoint(item);
  const manual = resolveManualPoint(item);
  const parts = [];
  // 使用中文状态标签
  if (item.status) parts.push(STATUS_LABELS[item.status] || item.status);
  if (auto) {
    parts.push(formatLngLat(auto));
    if (hasMeaningfulManualPoint(auto, manual)) parts.push(`手工 ${formatLngLat(manual)}`);
  }
  return parts.filter(Boolean).join(' ');
}
